/**
 * Tâche 1163 (Eric 2026-06-17) — Prévenir le doublon AU MOMENT de la saisie.
 *
 * Découvrir trois semaines plus tard, dans « Détecter les doublons », qu'on a
 * créé « Analyse des besoins financiers » alors que « Analyse des besoins »
 * existait coûte une fusion et une définition à réécrire. Ici, l'avertissement
 * arrive avant l'enregistrement — et il n'empêche RIEN : deux termes proches
 * peuvent légitimement coexister, c'est l'humain qui sait.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Warning } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export const SimilarTermWarning = ({ term, excludeId = "", onResult }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const candidate = (term || "").trim();
    // Deux lettres suffisent : « FE », « PV », « AG » sont des termes réels.
    if (candidate.length < 2) {
      setData(null);
      if (onResult) onResult(null);
      return undefined;
    }
    let cancelled = false;
    // Le mot s'écrit lettre par lettre : on attend que la frappe se calme.
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API}/api/jargon/similar?term=${encodeURIComponent(candidate)}` +
            (excludeId ? `&exclude_id=${encodeURIComponent(excludeId)}` : ""),
          { withCredentials: true }
        );
        if (!cancelled) {
          setData(res.data);
          if (onResult) onResult(res.data);
        }
      } catch {
        if (!cancelled) {
          setData(null);
          if (onResult) onResult(null);
        }
      }
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `onResult` volontairement hors dépendances : la fonction est recréée à
    // chaque rendu du parent et relancerait la requête en boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, excludeId]);

  if (!data?.matches?.length) return null;

  return (
    <div
      className={`mt-2 rounded-lg border px-3 py-2 text-xs ${
        data.has_identical
          ? "border-red-300 bg-red-50 text-red-900"
          : "border-amber-300 bg-amber-50 text-amber-900"
      }`}
      data-testid="similar-term-warning"
    >
      <p className="flex items-center gap-1.5 font-semibold">
        <Warning size={14} />
        {data.has_identical
          ? "Ce terme existe déjà, à la graphie près"
          : "Un terme très proche existe déjà"}
      </p>
      <ul className="mt-1 space-y-1">
        {data.matches.map((m) => (
          <li key={m.id} data-testid={`similar-term-${m.id}`}>
            <span className="font-medium">{m.term}</span>
            <span className="opacity-75"> — {m.reason}</span>
          </li>
        ))}
      </ul>
      <p className="mt-1 opacity-75">
        {data.has_identical
          ? "Enrichissez la fiche existante plutôt que d'en créer une seconde."
          : "Vous pouvez enregistrer quand même : deux termes voisins peuvent coexister."}
      </p>
    </div>
  );
};

export default SimilarTermWarning;
